<template>
  <div class="max-w-4xl mx-auto pb-10 animate-fade-in">

    <div class="mb-8">
      <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight">Mi Perfil</h1>
      <p class="text-slate-500 dark:text-slate-400 text-sm mt-1">Administra tu información personal y seguridad de la cuenta.</p>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-12 gap-8">

      <div class="lg:col-span-3">
        <nav class="flex lg:flex-col gap-2">
          <button
            @click="activeTab = 'personal'"
            class="flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200"
            :class="activeTab === 'personal'
              ? 'bg-brand-50 text-white dark:bg-brand-500/10 dark:text-white'
              : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800'"
          >
            <User class="w-4 h-4" /> Información Personal
          </button>

          <button
            @click="activeTab = 'security'"
            class="flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-all duration-200"
            :class="activeTab === 'security'
              ? 'bg-brand-50 text-white dark:bg-brand-500/10 dark:text-white'
              : 'text-slate-600 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-800'"
          >
            <Shield class="w-4 h-4" /> Seguridad
          </button>
        </nav>
      </div>

      <div class="lg:col-span-9">

        <div v-if="activeTab === 'personal'" class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 md:p-8 shadow-sm">

          <div class="flex flex-col sm:flex-row items-center gap-6 mb-8 border-b border-slate-100 dark:border-slate-800 pb-8">
            <div class="relative group">
              <div class="w-24 h-24 rounded-full overflow-hidden border-4 border-slate-50 dark:border-slate-800 shadow-md">
                <img
                  :src="profileForm.photo_url || 'https://i.pravatar.cc/300?u=default'"
                  class="w-full h-full object-cover transition-opacity"
                  :class="{ 'opacity-50': isUploadingPhoto }"
                />
              </div>

              <label class="absolute bottom-0 right-0 p-2 bg-slate-900 text-white rounded-full cursor-pointer hover:bg-brand-600 transition-colors shadow-lg">
                <Loader2 v-if="isUploadingPhoto" class="w-4 h-4 animate-spin" />
                <Camera v-else class="w-4 h-4" />
                <input type="file" class="hidden" accept="image/*" @change="handlePhotoUpload" />
              </label>
            </div>

            <div class="text-center sm:text-left">
              <h3 class="text-lg font-bold text-slate-900 dark:text-white">Foto de Perfil</h3>
              <p class="text-xs text-slate-500 max-w-xs mt-1">
                Sube una imagen (JPG, PNG). Se recomienda 400x400px. Esta foto será visible para tu equipo.
              </p>
            </div>
          </div>

          <form @submit.prevent="updateProfile" class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

              <div class="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-100 dark:border-slate-800">
                <div>
                  <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Usuario</label>
                  <div class="flex items-center gap-2 text-slate-400">
                    <User class="w-4 h-4" />
                    <span class="font-mono text-sm">{{ profileForm.username }}</span>
                    <Lock class="w-3 h-3 ml-auto opacity-50" />
                  </div>
                </div>
                <div>
                  <label class="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">Correo Electrónico</label>
                  <div class="flex items-center gap-2 text-slate-400">
                    <Mail class="w-4 h-4" />
                    <span class="font-mono text-sm">{{ profileForm.email }}</span>
                    <Lock class="w-3 h-3 ml-auto opacity-50" />
                  </div>
                </div>
              </div>

              <div class="md:col-span-2">
                <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Nombre Completo</label>
                <input
                  v-model="profileForm.full_name"
                  type="text"
                  class="form-input-premium"
                  placeholder="Tu nombre legal"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Documento ID</label>
                <input
                  v-model="profileForm.document"
                  type="text"
                  class="form-input-premium"
                  placeholder="DUI / Cédula"
                />
              </div>

              </div>

            <div class="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-800">
              <button
                type="submit"
                :disabled="isSaving || isUploadingPhoto"
                class="flex items-center gap-2 px-6 py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl shadow-lg shadow-brand-500/30 transition-all disabled:opacity-70 disabled:cursor-not-allowed"
              >
                <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
                <Save v-else class="w-4 h-4" />
                Guardar Cambios
              </button>
            </div>
          </form>
        </div>

        <div v-if="activeTab === 'security'" class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 md:p-8 shadow-sm animate-fade-in">

          <div class="flex items-start gap-4 mb-8">
            <div class="p-3 bg-brand-50 dark:bg-brand-900/20 rounded-xl">
              <Shield class="w-6 h-6 text-white dark:text-white" />
            </div>
            <div>
              <h3 class="text-lg font-bold text-slate-900 dark:text-white">Cambiar Contraseña</h3>
              <p class="text-sm text-slate-500">Asegúrate de usar una contraseña segura que no hayas usado en otros sitios.</p>
            </div>
          </div>

          <form @submit.prevent="changePassword" class="space-y-5 max-w-lg">

            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Contraseña Actual</label>
              <input
                v-model="passwordForm.current_password"
                type="password"
                required
                class="form-input-premium"
                placeholder="••••••••"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Nueva Contraseña</label>
              <input
                v-model="passwordForm.new_password"
                type="password"
                required
                class="form-input-premium"
                placeholder="••••••••"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Confirmar Nueva Contraseña</label>
              <input
                v-model="passwordForm.confirm_password"
                type="password"
                required
                class="form-input-premium"
                placeholder="••••••••"
              />
            </div>

            <div class="pt-4">
              <button
                type="submit"
                :disabled="isSaving"
                class="flex items-center gap-2 px-6 py-2.5 bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200 text-white font-bold rounded-xl transition-all disabled:opacity-70"
              >
                <Loader2 v-if="isSaving" class="w-4 h-4 animate-spin" />
                <CheckCircle2 v-else class="w-4 h-4" />
                Actualizar Contraseña
              </button>
            </div>

          </form>
        </div>

      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, reactive, onMounted } from 'vue';
import {
  User, Lock, Mail, Shield, Camera, Save, Loader2, AlertCircle, CheckCircle2
} from 'lucide-vue-next';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';
import { useUpload } from '@/composables/useUpload';
import Swal from 'sweetalert2';

// Composable de Imagen
const { uploading: isUploadingPhoto, uploadFile } = useUpload();

// Estado
const activeTab = ref('personal'); // 'personal' | 'security'
const isLoading = ref(false);
const isSaving = ref(false);
const currentUserId = ref(null);

// Formulario de Datos Personales
const profileForm = reactive({
  username: '', // Read-only
  email: '',    // Read-only
  full_name: '',
  document: '',
  phone: '',    // Si decides agregarlo a la BD después
  photo_url: ''
});

// Formulario de Contraseña
const passwordForm = reactive({
  current_password: '',
  new_password: '',
  confirm_password: ''
});

// 1. Cargar Datos del Usuario
onMounted(async () => {
  isLoading.value = true;
  try {
    const sessionUser = SecureStorage.getItem('neurals_user');
    if (!sessionUser) throw new Error("Sesión no válida");

    currentUserId.value = sessionUser.id;

    // Hacemos GET fresco al servidor para tener los datos más recientes
    const response = await apiClient.get(`/users/${currentUserId.value}`); // Asumiendo endpoint GET /users/{id}
    const data = response.data; // O response.data.data según tu estructura

    Object.assign(profileForm, {
      username: data.username,
      email: data.email,
      full_name: data.full_name,
      document: data.document,
      photo_url: data.photo_url // Asegúrate de que tu backend envíe este campo (ver nota abajo)
    });

  } catch (error) {
    console.error(error);
    Swal.fire('Error', 'No se pudo cargar la información del perfil', 'error');
  } finally {
    isLoading.value = false;
  }
});

// 2. Subir Foto de Perfil
const handlePhotoUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  // Subimos a carpeta "avatars"
  const url = await uploadFile(file, "/user_avatars");

  if (url) {
    profileForm.photo_url = url;
  }
  event.target.value = ''; // Reset input
};

// 3. Guardar Perfil (Datos Básicos)
const updateProfile = async () => {
  isSaving.value = true;
  try {
    // PUT /users/{id}
    await apiClient.put(`/users/${currentUserId.value}`, {
      full_name: profileForm.full_name,
      document: profileForm.document, // <--- ¡FALTABA ESTO!
      photo_url: profileForm.photo_url
    });

    // ACTUALIZAR SESIÓN LOCAL
    const currentUser = SecureStorage.getItem('neurals_user');
    if (currentUser) {
      currentUser.full_name = profileForm.full_name;
      // currentUser.photo_url = profileForm.photo_url; // Opcional
      SecureStorage.setItem('neurals_user', currentUser);
    }

    Swal.fire({
      icon: 'success',
      title: 'Perfil actualizado',
      toast: true,
      position: 'top-end',
      timer: 3000,
      showConfirmButton: false
    });

  } catch (error) {
    console.error(error);
    Swal.fire('Error', 'No se pudo actualizar el perfil', 'error');
  } finally {
    isSaving.value = false;
  }
};

// 4. Cambiar Contraseña
// 4. Cambiar Contraseña (Adaptado a tu Backend PHP)
const changePassword = async () => {
  // Validación básica
  if (passwordForm.new_password !== passwordForm.confirm_password) {
    Swal.fire('Error', 'Las contraseñas nuevas no coinciden', 'warning');
    return;
  }

  if (passwordForm.new_password.length < 6) {
    Swal.fire('Error', 'La contraseña debe tener al menos 6 caracteres', 'warning');
    return;
  }

  isSaving.value = true;
  try {
    await apiClient.put(`/users/${currentUserId.value}`, {
      password: passwordForm.new_password
    });

    Swal.fire('Éxito', 'Contraseña actualizada correctamente', 'success');

    // Limpiar formulario
    passwordForm.current_password = ''; // Aunque no se usó, lo limpiamos por UI
    passwordForm.new_password = '';
    passwordForm.confirm_password = '';

  } catch (error) {
    console.error(error);
    const msg = error.response?.data?.message || 'Error al actualizar la contraseña';
    Swal.fire('Error', msg, 'error');
  } finally {
    isSaving.value = false;
  }
};
</script>
<style scoped>
@keyframes fade-in {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}
.animate-fade-in {
  animation: fade-in 0.3s ease-out forwards;
}
</style>